const clamp01 = (x) => Math.max(0, Math.min(1, x));

const asNumber = (v) => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

export function isValidMatrix(matrix) {
  return Array.isArray(matrix) && matrix.length > 0 && Array.isArray(matrix[0]);
}

export function sumMatrix(matrix) {
  if (!isValidMatrix(matrix)) return 0;
  let sum = 0;
  for (const row of matrix) {
    for (const v of row || []) sum += asNumber(v);
  }
  return sum;
}

export function sumDiagonal(matrix) {
  if (!isValidMatrix(matrix)) return 0;
  let sum = 0;
  for (let i = 0; i < matrix.length; i += 1) sum += asNumber(matrix?.[i]?.[i]);
  return sum;
}

export function computeAccuracyPct(matrix) {
  const total = sumMatrix(matrix);
  if (total <= 0) return 0;
  return (sumDiagonal(matrix) / total) * 100;
}

export function computeRowSums(matrix) {
  if (!isValidMatrix(matrix)) return [];
  return matrix.map((row) => (row || []).reduce((acc, v) => acc + asNumber(v), 0));
}

export function getTopLabelIndicesBySupport(matrix, topN) {
  if (!isValidMatrix(matrix) || !Number.isFinite(topN) || topN <= 0) return [];
  const sums = computeRowSums(matrix);
  const indices = sums.map((support, idx) => ({ idx, support }));
  indices.sort((a, b) => b.support - a.support);
  return indices.slice(0, Math.min(topN, indices.length)).map((x) => x.idx);
}

export function sliceLabelsAndMatrix(labels, matrix, indices) {
  if (!Array.isArray(labels) || !isValidMatrix(matrix) || !Array.isArray(indices)) {
    return { labels: [], matrix: [] };
  }
  // IMPORTANT: keep label order aligned with matrix slicing order.
  // `indices` is typically sorted (e.g. by support desc). If we only "filter"
  // labels in original order while slicing the matrix in `indices` order,
  // row/col headers no longer correspond to the cell values.
  const filteredLabels = indices.map((i) => labels[i]).filter((x) => x !== undefined);
  const filteredMatrix = indices.map((i) => indices.map((j) => asNumber(matrix?.[i]?.[j])));
  return { labels: filteredLabels, matrix: filteredMatrix };
}

export function getMaxByPredicate(matrix, predicate) {
  if (!isValidMatrix(matrix)) return 0;
  let max = 0;
  for (let i = 0; i < matrix.length; i += 1) {
    const row = matrix[i] || [];
    for (let j = 0; j < row.length; j += 1) {
      if (!predicate(i, j)) continue;
      max = Math.max(max, asNumber(row[j]));
    }
  }
  return max;
}

/**
 * Restrict a label-vs-label matrix to one actual (true) label.
 * Predicted columns stay so misclassifications of that label remain visible.
 * Returns { rowLabels, colLabels, matrix }.
 */
export function sliceMatrixToActualLabel(labels, matrix, selectedLabel) {
  if (selectedLabel == null || selectedLabel === '') {
    return { rowLabels: labels || [], colLabels: labels || [], matrix };
  }
  if (!Array.isArray(labels) || !isValidMatrix(matrix)) {
    return { rowLabels: [], colLabels: [], matrix: [] };
  }
  const rowIdx = labels.findIndex((lab) => String(lab) === String(selectedLabel));
  if (rowIdx < 0) {
    return { rowLabels: [], colLabels: [], matrix: [] };
  }
  const row = matrix[rowIdx] || [];
  return {
    rowLabels: [labels[rowIdx]],
    colLabels: labels,
    matrix: [labels.map((_, j) => asNumber(row[j]))],
  };
}

/**
 * Restrict a label-vs-label matrix to one predicted label.
 * Actual columns stay so false positives for that prediction remain visible.
 * Laid out as Predicted ↓ / Actual → (one row, actuals as columns).
 * Returns { rowLabels, colLabels, matrix }.
 */
export function sliceMatrixToPredictedLabel(labels, matrix, selectedLabel) {
  if (selectedLabel == null || selectedLabel === '') {
    return { rowLabels: labels || [], colLabels: labels || [], matrix };
  }
  if (!Array.isArray(labels) || !isValidMatrix(matrix)) {
    return { rowLabels: [], colLabels: [], matrix: [] };
  }
  const colIdx = labels.findIndex((lab) => String(lab) === String(selectedLabel));
  if (colIdx < 0) {
    return { rowLabels: [], colLabels: [], matrix: [] };
  }
  return {
    rowLabels: [labels[colIdx]],
    colLabels: labels,
    matrix: [labels.map((_, i) => asNumber(matrix[i]?.[colIdx]))],
  };
}

function shadeForIntensity(shades, intensity) {
  if (!Array.isArray(shades) || shades.length === 0) return 'transparent';
  const last = shades.length - 1;
  const index = Math.min(last, Math.round(clamp01(intensity) * last));
  return shades[index];
}

export function getCountsCellStyle({ value, isDiagonal, maxDiagonal, maxOffDiagonal, colors }) {
  const v = asNumber(value);
  if (v <= 0) {
    return {
      background: 'var(--primitive-neutral-50)',
      color: 'var(--semantic-content-tertiary)',
      fontWeight: isDiagonal ? 600 : 400,
    };
  }

  const shades = isDiagonal ? colors.diagonalShades : colors.offDiagonalShades;
  const max = Math.max(1, isDiagonal ? maxDiagonal : maxOffDiagonal);
  return {
    background: shadeForIntensity(shades, v / max),
    color: 'var(--semantic-content-primary)',
    fontWeight: isDiagonal ? 600 : 400,
  };
}

function normalizeSingleModelPayload(p) {
  if (!p) return null;
  const labels = Array.isArray(p.labels) ? p.labels : null;
  const matrix = Array.isArray(p.matrix) ? p.matrix : null;
  if (!labels || !matrix) return null;
  return { labels, matrix };
}

function normalizeComparisonPayload(p) {
  if (!p) return null;
  const labelsOld = p?.labels?.oldModel;
  const labelsNew = p?.labels?.newModel;
  const matrixOld = p?.matrix?.oldModel;
  const matrixNew = p?.matrix?.newModel;
  if (!labelsOld && !labelsNew && !matrixOld && !matrixNew) return null;
  return {
    old: {
      labels: Array.isArray(labelsOld) ? labelsOld : null,
      matrix: Array.isArray(matrixOld) ? matrixOld : null,
    },
    new: {
      labels: Array.isArray(labelsNew) ? labelsNew : null,
      matrix: Array.isArray(matrixNew) ? matrixNew : null,
    },
  };
}

export function remapMatrixToLabels({ matrix, fromLabels, toLabels }) {
  if (!isValidMatrix(matrix) || !Array.isArray(fromLabels) || !Array.isArray(toLabels)) return null;
  const indexByLabel = new Map(fromLabels.map((lab, idx) => [String(lab), idx]));
  const n = toLabels.length;
  const out = new Array(n);
  for (let i = 0; i < n; i += 1) {
    out[i] = new Array(n);
    const fromI = indexByLabel.get(String(toLabels[i]));
    for (let j = 0; j < n; j += 1) {
      const fromJ = indexByLabel.get(String(toLabels[j]));
      if (fromI === undefined || fromJ === undefined) out[i][j] = 0;
      else out[i][j] = asNumber(matrix?.[fromI]?.[fromJ]);
    }
  }
  return out;
}

/**
 * Supports:
 * - single model: { labels: [...], matrix: [[...]] }
 * - comparison: { labels: { oldModel, newModel }, matrix: { oldModel, newModel } }
 *
 * Returns { old, new, labels } where old/new may be null.
 */
export function normalizeLabelConfusionPayload(payload) {
  const comparison = normalizeComparisonPayload(payload);
  if (comparison) {
    const labels = comparison.new.labels || comparison.old.labels || [];
    const old = comparison.old.labels && comparison.old.matrix
      ? {
        labels,
        matrix: remapMatrixToLabels({
          matrix: comparison.old.matrix,
          fromLabels: comparison.old.labels,
          toLabels: labels,
        }),
      }
      : null;
    const neu = comparison.new.labels && comparison.new.matrix
      ? {
        labels,
        matrix: remapMatrixToLabels({
          matrix: comparison.new.matrix,
          fromLabels: comparison.new.labels,
          toLabels: labels,
        }),
      }
      : null;
    return { old, new: neu, labels };
  }

  const single = normalizeSingleModelPayload(payload);
  if (single) {
    return { old: null, new: { labels: single.labels, matrix: single.matrix }, labels: single.labels };
  }

  return { old: null, new: null, labels: [] };
}

